<script setup lang="ts">
import { NuxtLink } from '#components'
import type { Props as ProductUiProps } from './ProductUiOptions.vue'

export type ProductUi = Partial<{
  id: string | None
  departmentName: string | None
  title: string
  description?: string | None
  imageBox: gen.ImageNodeItemFragment | None
  imageSrc?: string | None
  slug: string
  option: { key: string; uiKey?: string | None; uiValue?: string | None }[] | None
  discountMessage: string | None
  totalPrice: string | None
  totalPriceNum: number | None
  totalOld: string | None
  outOfStockMsg: string | None
  qty: number | None
  lineItems: gen.ProductListingBoxUiPriceFragment['lineItems'] | None
  endAt: gen.ProductListingBoxUiPriceFragment['endAt'] | None
}>

export type Props = {
  col?: boolean
  product: Partial<ProductUi> | None
  listing?: gen.ProductListingBoxFragment | null
  removeFromCart?: () => void
  isAdded?: boolean | null
  skipLink?: boolean
  setHeightOnOnmount?: boolean
  cartQty?: number
  cartZeroClose?: boolean
}

const t2 = useT2({
  'Last One in Stock': 'Paskutinis vienetas sandėlyje',
})
const props = defineProps<Props>()
const links = useLinks()
const mobile = useMobile()
const elem = useTemplateRef('elem')
const style = shallowRef<Record<string, string>>()
const timer = useTimeout()

const ui = computed(() => {
  const { listing, product } = props
  const slug = listing?.slug || product?.slug

  return {
    lastOne: !!listing?._values?.inStock && !!listing?._values?.stock && listing?._values.stock <= (listing.settingsQty?.min || 1),
    imageBox: getListingImg(listing) || product?.imageBox,
    imageSrc: product?.imageSrc,
    link: props.skipLink || !slug ? undefined : links.linkProduct({ slug }),
    total: helpers.toString(product?.totalPrice),
    old: product?.totalOld ? product?.totalOld : null,
    qty: helpers.isNumber(product?.qty) ? product?.qty : null,
    options: {
      title: listing?.title || helpers.toString(product?.title),
      description: product?.description,
      departmentName: listing?.departmentName || helpers.toString(product?.departmentName),
      option: listing?.option || helpers.arr(product?.option),
      discountMsg: product?.discountMessage,
      outOfStockMsg: undefined, // product?.outOfStockMsg,
    } satisfies ProductUiProps,
  }
})

function remove() {
  props.removeFromCart?.()
}
watch(
  () => (props.cartZeroClose ? props.cartQty : undefined),
  (qty) => {
    if (typeof qty !== 'number') return

    timer.set(() => {
      if (qty !== 0) style.value = {}
      else {
        const h = myElem(elem.value, 'scrollHeight', 0)
        if (h) style.value = { height: h + 'px' }
        timer.set(() => (style.value = { height: '0px', overflow: 'hidden' }), 10)
      }
    }, 0)
  }
)

const slots = useSlots()
const mobileSlotBuy = computed((): boolean => mobile.value && !!(props.removeFromCart || slots.buy?.({})?.length))
</script>

<template>
  <div ref="elem" :class="[$style.cart, { col }]" :style>
    <div :class="$style.cont">
      <component :is="ui.link ? NuxtLink : 'div'" :to="ui.link" class="_relative d_mb2 m_mb04 _wm100p _block">
        <div v-if="isAdded" :class="$style.added" />
        <BaseImage ratio="square" sizes="fixed:860" :lazy="true" :class="!col && '_br6'" :img="ui?.imageBox" :src="ui?.imageBox ? null : ui?.imageSrc" />
        <div v-if="product?.outOfStockMsg" :class="$style.messages">
          <UiMsg red :txt="product?.outOfStockMsg" />
        </div>
      </component>

      <div class="_col m_pr04 _w100p _mb2" :class="col && '_mb1 m_pl04'">
        <div v-if="mobile" class="_row _ctrY _mb04 _end" :class="[col && '_pt02 _pb04']">
          <s v-if="ui.old" class="_mono _fs08 _pr08">{{ ui.old }}</s>

          <div class="_fw-l _row _inline _ctrY">
            <IconSlash :curr="ui.qty" :max="ui.total" max-price :red="!!ui.old" />
          </div>
        </div>

        <component :is="ui.link ? NuxtLink : 'div'" :to="ui.link" :class="$style.link">
          <div class="d_pr2 d_pt02">
            <ProductUiOptions v-bind="ui.options">
              <template v-if="product?.lineItems?.length || product?.endAt" #messagesTop>
                <div class="_col">
                  <template v-if="product?.lineItems?.length === 1">
                    <template v-for="(uiPrice, index) of product?.lineItems" :key="index">
                      <div v-if="uiPrice?.ui?.length || product?.endAt" class="_row _wrap _gap06">
                        <template v-for="discount in uiPrice?.ui" :key="discount.amount">
                          <UiMsg v-if="discount?.msg" :txt="discount.amount" :sub="discount.msg" />
                        </template>
                        <ClientOnly v-if="product?.endAt"><LazyProductEndsIn :end-at="product?.endAt" /></ClientOnly>
                      </div>
                    </template>
                  </template>

                  <template v-if="(product?.lineItems?.length || 0) > 1">
                    <div v-for="(line, index) of product?.lineItems" :key="index" class="_row _gap06 _pt1 _ctrY">
                      <span class="_fw-xl _fs08">{{ line.qty }}</span>
                      <span class="_fw-xl _fs07">&times;</span>
                      <span class="_mono">{{ line.price.priceForHumans }}</span>
                      <template v-for="discount in line?.ui" :key="discount.amount">
                        <UiMsg v-if="discount?.msg" :txt="discount.amount" :sub="discount.msg" />
                      </template>
                    </div>
                  </template>
                </div>
              </template>

              <template v-if="$slots.messages" #messages><slot name="messages" /></template>
            </ProductUiOptions>

            <span v-if="ui.lastOne" class="_label _red _row _ctry _gap04 _fs08"><IconExclamation class="_svg14" />{{ t2('Last One in Stock') }}</span>
          </div>
        </component>

        <div v-if="!mobile" class="_row _wrap _pt08 _fw-l _mta _c1 _stretch _spaced">
          <div class="_col _ctrY _flex _gap06">
            <slot name="buy" />
          </div>

          <div class="_col _gap06 _pt04 _pb04 _txt_end _end _endA">
            <s v-if="ui.old" class="_mono _fs09">{{ ui.old }}</s>

            <div class="_wrap _flexblock _baseline _end">
              <IconSlash v-if="ui.qty" :curr="ui.qty" :max="ui.total" max-price :red="!!ui.old" />
              <template v-else>
                <UiPriceCounter v-if="product?.totalPriceNum" :price="product?.totalPriceNum" :class="ui.old && '_red'" />
                <b v-else class="_mono _fs09" :class="[ui.old && '_red']">{{ ui.total }}</b>
              </template>
            </div>
          </div>
        </div>
      </div>
    </div>

    <slot name="actions" />

    <ButtonIcon v-if="removeFromCart && !mobile" :class="[$style.exit, $style.showOnHover]" :txt="t2('Remove')" @click.passive="remove" />

    <div v-if="mobileSlotBuy" class="_pb1">
      <div class="_row _ctry _gap1 _wrap _h44 d_mt1 m_mt04 m_mb1">
        <slot name="buy" />
      </div>
    </div>
  </div>
</template>

<style module>
.messages {
  composes: global(_absolute);
  left: 0px;
  bottom: 4px;
  max-width: 130%;
}
.cart {
  composes: global(_relative);
  composes: global(m_col);
  transition: height 180ms var(--tran1);
  max-width: 680px;
}
:global(.m) .cart {
  display: flex;
}

.cont {
  composes: global(_relative);
  composes: global(_w100p);
  composes: global(_gap2);

  composes: global(_grid);
  grid-template-columns: minmax(0, 2.5fr) minmax(0, 7fr);
}

:global(.m) .cont {
  grid-template-columns: minmax(50px, 30%) minmax(0, 1fr);
}

.cart:global(.col) .cont {
  grid-template-columns: 1fr;
  gap: 0;
}

.exit {
  composes: global(_absolute);
  composes: global(_b5);

  right: 0;
  top: 0;
  z-index: 1;
  background: rgb(255 255 255 / 0.98);
}

.added {
  composes: global(_absolute);
  top: 5px;
  left: 0px;
  z-index: 1;

  composes: global(_h28);
  composes: global(_w28);
  composes: global(_ctr);

  composes: global(_c1);

  composes: global(_bg0);
}
.added::after,
.added::before {
  background: currentColor;
  content: '';
  display: block;
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  margin: auto;
}
.added::after {
  height: 2px;
  width: 14px;
}
.added::before {
  height: 14px;
  width: 2px;
}

:global(.d) .showOnHover {
  opacity: 0.01;
}
:global(.d) .cart:hover .showOnHover {
  opacity: 0.99;
}

.link:hover {
  color: var(--blue2);
}
</style>
